import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { UserContext, RoleLevel } from '../models/user-context.model';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class UserContextService {
  private readonly http = inject(HttpClient);
  private readonly _userContext = signal<UserContext | null>(null);

  readonly userContext = this._userContext.asReadonly();

  async loadUserContext(): Promise<void> {
    try {
      const ctx = await firstValueFrom(
        this.http.get<UserContext>(`${environment.apiBaseUrl}/marques/me`)
      );
      this._userContext.set(ctx);
    } catch (err) {
      // Erreur non bloquante : l'app reste accessible sans contexte enrichi
      console.error('Impossible de charger le UserContext:', err);
    }
  }

  get isAdmin(): boolean {
    return this._userContext()?.is_admin ?? false;
  }

  get userEmail(): string {
    return this._userContext()?.email ?? '';
  }

  canRead(region?: string): boolean {
    const ctx = this._userContext();
    if (!ctx) return false;
    if (ctx.is_admin) return true;
    if (!region) return Object.keys(ctx.permissions).length > 0;
    return region in ctx.permissions;
  }

  canWrite(region?: string): boolean {
    const ctx = this._userContext();
    if (!ctx) return false;
    if (ctx.is_admin) return true;
    // Strict equality : AP (RoleLevel 3) ne doit PAS pouvoir éditer (AC2/AC3)
    if (!region) return Object.values(ctx.permissions).some((r) => r === RoleLevel.EDITEUR);
    return ctx.permissions[region] === RoleLevel.EDITEUR;
  }

  canCreate(region?: string): boolean {
    return this.canWrite(region);
  }

  canDelete(region?: string): boolean {
    return this.canWrite(region);
  }

  canUploadLogo(region?: string): boolean {
    const ctx = this._userContext();
    if (!ctx) return false;
    if (ctx.is_admin) return true;
    // AP (RoleLevel 3) inclus — FR-14 : Éditeur, AP ou OS&A
    if (!region) return Object.values(ctx.permissions).some((r) => r >= RoleLevel.EDITEUR);
    return (ctx.permissions[region] ?? 0) >= RoleLevel.EDITEUR;
  }

  /**
   * Éditeur, AP ou OS&A peuvent modifier les données marques (FR-19).
   * AP (RoleLevel 3) est inclus — contrairement à canWrite() (strict Éditeur).
   */
  canModifyMarque(region?: string): boolean {
    const ctx = this._userContext();
    if (!ctx) return false;
    if (ctx.is_admin) return true;
    if (!region) return Object.values(ctx.permissions).some((r) => r >= RoleLevel.EDITEUR);
    return (ctx.permissions[region] ?? 0) >= RoleLevel.EDITEUR;
  }

  canAccessConfig(): boolean {
    return this.isAdmin;
  }
}
